/*
 * Contact lookup component
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2021 Gallagher Bassett Services, Inc.
 *
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  input,
  Input,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import {
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors
} from '@angular/forms';
import {
  Contact,
  ContactType
} from 'app/shared/models';
import { NotificationService } from 'app/shared/services/notification.service';

@Component ( {
  selector: 'reporter-contact-lookup',
  templateUrl: './contact-lookup.component.html',
  styleUrls: [ './contact-lookup.component.scss' ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef ( () => ContactLookupComponent ),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef ( () => ContactLookupComponent ),
      multi: true
    },
  ],
  standalone: false
} )
export class ContactLookupComponent implements AfterViewInit {

  @Input () incidentId: number;
  @Input () locationId: string;
  @Input () contactType: ContactType;
  @Input () readOnly = false;
  @Input () showEmployeeName = false;
  @Input () useEmployeeId = false;
  @Input () useEmployeeLookup = false;
  @Input () showLookup = true; // When false show a text field only
  @Input () maxlength: number;
  @Output () contactChange = new EventEmitter<Contact> ();
  typeInValue: string;
  selected: Contact;
  showSearch = false;

  constructor (
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private notificationService: NotificationService,
  ) {
  }

  set value ( v: any ) {
    if ( v !== this.innerValue ) {
      this.innerValue = v;
      this.onChange ( v );
    }
  }

  get value () {
    return this.innerValue;
  }

  get required (): boolean {
    return this.elementRef.nativeElement.attributes.hasOwnProperty ( 'required' );
  }

  innerValue: any;
  initialized = false;

  @ViewChild ( 'field', { static: true } ) field: ElementRef;
  onChange = ( _: any ) => {
  };
  onTouched = () => {
  };

  ngAfterViewInit () {
    if ( this.required && this.field ) {
      this.renderer.setAttribute ( this.field.nativeElement, 'required', '' );
    }
    if ( this.elementRef.nativeElement.classList.contains ( 'required' ) && this.field ) {
      this.renderer.addClass ( this.field.nativeElement, 'required' );
    }
    this.initialized = true;

    if ( this.value && !this.typeInValue ) {
      this.typeInValue = this.value;
    }
  }

  writeValue ( value: any ): void {
    this.innerValue = value;
    this.onChange ( value );
    this.onTouched ();
  }

  registerOnChange ( fn: ( value: any ) => any ): void {
    this.onChange = fn;
  }

  registerOnTouched ( fn: () => any ): void {
    this.onTouched = fn;
  }

  setDisabledState ( isDisabled: boolean ): void {
    this.renderer.setProperty ( this.elementRef.nativeElement, 'disabled', isDisabled );
  }

  validate ( control: FormControl ): ValidationErrors {
    if ( !this.initialized ) {
      return null;
    }
    if ( ( control.value === null || control.value === undefined ) && this.required ) {
      return {
        required: true,
        message: ' is required.'
      };
    }
    return null;
  }

  clear () {
    this.value = undefined;
    this.selected = undefined;
    this.typeInValue = undefined;
    this.notificationService.clearAll ();
  }

  select ( contact: any ) {
    this.selected = contact;
    let value = this.useEmployeeId ? contact.id : contact.employeeNumber;
    if ( contact.contactType === 'MEDICAL' ) {
      value = contact.companyName;
    }
    this.typeInValue = value;
    if ( this.showEmployeeName ) {
      this.typeInValue = `${ contact.firstName } ${ contact.lastName }`;
    }
    this.writeValue ( value );
    this.contactChange.emit ( contact );
    this.close ();
  }

  close () {
    this.showSearch = false;
    this.notificationService.clearAll ();
  }
}
